import { Controller } from "stimulus"

// Builds a natural-language job-search intent from user-selected tags.
// As the user toggles category / seniority / remote chips, the intent
// sentence is assembled and written to the text input so they can see
// (and optionally edit) what will be sent to /jobs/discover.
//
// Targets:
//   - input: the text field that holds the assembled sentence
//   - chip: all selectable chips (multiple)
//   - summary: optional element that shows the assembled sentence as a preview
//
// Chip data attributes:
//   data-group="category" | "seniority" | "remote"
//   data-value="ai_content" | "senior" | "remote" | ...
//   data-multi="true" for categories (multiple allowed); omit for single-select groups
export default class extends Controller {
  static targets = ["input", "chip", "summary"]

  connect() {
    this.selections = { category: [], seniority: [], remote: [] }
    this.seed()
  }

  // Pre-select chips if the input already has text on load (e.g. user
  // came back via "Edit intent"). For now we just clear.
  seed() {
    this.render()
  }

  toggle(e) {
    e.preventDefault()
    e.stopPropagation()
    const chip = e.currentTarget
    const group = chip.dataset.group
    const value = chip.dataset.value
    const multi = chip.dataset.multi === "true"

    if (multi) {
      const idx = this.selections[group].indexOf(value)
      if (idx >= 0) {
        this.selections[group].splice(idx, 1)
      } else {
        this.selections[group].push(value)
      }
    } else {
      this.selections[group] = (this.selections[group] === value) ? null : value
    }
    this.render()
  }

  render() {
    // Paint active state on chips
    this.chipTargets.forEach(chip => {
      const group = chip.dataset.group
      const value = chip.dataset.value
      const active = Array.isArray(this.selections[group])
        ? this.selections[group].includes(value)
        : this.selections[group] === value
      chip.classList.toggle('active', active)
    })

    // Assemble the natural-language sentence and write it to the input
    const sentence = this.buildSentence()
    if (this.hasInputTarget) {
      const input = this.inputTarget
      input.value = sentence

      // Dispatch an input event so the rotating-placeholder controller
      // picks up the value change and hides its overlay span.
      input.dispatchEvent(new Event('input', { bubbles: true }))

      // Auto-resize textarea to fit content
      input.style.height = 'auto'
      input.style.height = input.scrollHeight + 'px'

      // On mobile, scroll the input into view so the user sees the update
      if (window.innerWidth < 993 && sentence) {
        input.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
    if (this.hasSummaryTarget) {
      this.summaryTarget.textContent = sentence || "Pick a few tags above to build your search…"
      this.summaryTarget.classList.toggle('opacity-5', !sentence)
    }
  }

  buildSentence() {
    const seniorityMap = { junior: "junior-level", mid: "mid-level", senior: "senior", lead: "lead", exec: "executive" }
    const remoteMap = { remote: "remote", hybrid: "hybrid", onsite: "onsite" }

    const prettify = (c) => c.split('_').map(part => {
      if (part === 'ai' || part === 'ml') return part.toUpperCase()
      return part[0].toUpperCase() + part.slice(1)
    }).join(' ')

    const categoryLabels = this.selections.category.map(prettify)
    const seniorityLabels = this.selections.seniority.map(s => seniorityMap[s] || s)
    const remoteLabels = this.selections.remote.map(r => remoteMap[r] || r)

    if (!categoryLabels.length && !seniorityLabels.length && !remoteLabels.length) return ""

    const parts = []
    parts.push("I'm looking for")

    if (seniorityLabels.length) parts.push(` ${seniorityLabels.join(' or ')}`)
    if (categoryLabels.length) {
      parts.push(` ${categoryLabels.join(', ')} roles`)
    } else {
      parts.push(` roles`)
    }
    if (remoteLabels.length) parts.push(`, ${remoteLabels.join(' or ')}`)

    return parts.join('')
  }

  // Called on form submit — hide the onboarding wizard and reveal
  // the chat messages area so it looks like a chat from this point on.
  onSubmit() {
    // Hide the onboarding (tag picker, heading, etc.)
    const onboarding = document.getElementById('ai-jobs-onboarding')
    if (onboarding) onboarding.style.display = 'none'

    // Hide the left-inner wrapper
    const leftInner = document.querySelector('.ai-jobs-left-inner')
    if (leftInner) leftInner.style.display = 'none'

    // Move the form from the onboarding area into the chat input slot
    const form = document.querySelector('turbo-frame#update-thread-form')
    const slot = document.getElementById('ai-jobs-chat-input-slot')
    if (form && slot) {
      slot.appendChild(form)
    }

    // Inject the chat header
    const headerSlot = document.getElementById('ai-jobs-chat-header-slot')
    if (headerSlot && !headerSlot.querySelector('.ai-jobs-chat-header')) {
      headerSlot.innerHTML = `
        <div class="ai-jobs-chat-header d-flex align-items-center justify-content-between">
          <div>
            <span class="fad fa-briefcase text-color-highlight mr-2"></span>
            <span class="font-size-14 bold">Writing.io Job Finder</span>
          </div>
          <a class="btn btn-link btn-sm text-color-highlight p-0" href="/jobs?reset_chat=true">
            <span class="far fa-plus mr-1"></span>
            New search
          </a>
        </div>
      `
    }

    // Reveal the chat area
    const chat = document.querySelector('.ai-jobs-chat')
    if (chat) {
      chat.style.height = '100%'
      chat.style.overflow = ''
      chat.style.border = ''
      chat.style.minHeight = ''
      chat.style.padding = ''
      chat.style.margin = ''
      chat.style.flex = '1 1 0%'
      chat.style.display = 'flex'
      chat.style.flexDirection = 'column'
    }

    // After a short delay (let memory extraction + embedding run),
    // refresh the right column to show the My Jobs personalized feed.
    setTimeout(() => {
      const frame = document.getElementById('ai-jobs-content')
      if (frame) {
        frame.src = '/jobs/my_jobs'
      }
    }, 3000)
  }
}
